import { useRef, useEffect } from 'react';
import { Spinner, LoadingState, ErrorState } from '../ui';
import { MessageBubble, type Message } from './MessageBubble';
import { ProposalBubble, type Proposal, type ProposalReviewActions } from './ProposalBubble';

// Union type for timeline items
type TimelineItem =
  | { type: 'message'; data: Message; timestamp: string }
  | { type: 'proposal'; data: Proposal; timestamp: string };

/** Сколько ждём черновик, прежде чем убрать индикатор: движок мог упасть. */
const DRAFT_WAIT_TIMEOUT_MS = 60_000;

interface ShadowDialogueProps {
  messages: Message[];  // All messages (will filter to client only)
  proposals: Proposal[];
  isLoading: boolean;
  error?: Error | null;
  reviewActions?: ProposalReviewActions | undefined;
}

/**
 * Shadow Dialogue — параллельный диалог без права отправки.
 *
 * Показывает:
 * - Client messages (входящие сообщения)
 * - Proposals (гипотезы системы)
 *
 * НЕ показывает:
 * - Operator messages (они только в Real)
 */
export function ShadowDialogue({
  messages,
  proposals,
  isLoading,
  error,
  reviewActions,
}: ShadowDialogueProps): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);

  // Filter to client messages only (operator messages stay in Real only)
  const clientMessages = messages.filter(m => !m.isOutbound);

  // Build timeline: client messages + proposals, sorted by time
  const timeline: TimelineItem[] = [
    ...clientMessages.map(m => ({
      type: 'message' as const,
      data: m,
      timestamp: m.timestamp,
    })),
    ...proposals.map(p => ({
      type: 'proposal' as const,
      data: p,
      timestamp: p.createdAt,
    })),
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  // Ждём черновик: последнее в таймлайне — сообщение клиента, значит движок
  // либо ещё в очереди, либо генерирует. Опираемся на факт «черновика нет»,
  // а не на статус thinking: он живёт около секунды и в поллинг почти не попадает.
  //
  // Ограничение по времени обязательно: если движок упал или сообщение
  // отфильтровано гардами, черновика не будет никогда — а вечный спиннер
  // утверждал бы, что AI работает. Через минуту молчания просто убираем его.
  const lastItem = timeline[timeline.length - 1];
  const awaitingDraft =
    lastItem?.type === 'message' &&
    Date.now() - new Date(lastItem.timestamp).getTime() < DRAFT_WAIT_TIMEOUT_MS;

  useEffect(() => {
    if (timeline.length > 0 && endRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [timeline.length, awaitingDraft]);

  if (isLoading && timeline.length === 0) {
    return (
      <LoadingState />
    );
  }

  if (error) {
    return (
      <ErrorState error={error} />
    );
  }

  if (timeline.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-text-secondary p-4">
        <div className="w-16 h-16 rounded-full bg-surface-subtle flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-accent-ai/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
        </div>
        <p className="text-sm text-center">Shadow диалог</p>
        <p className="text-xs text-text-secondary text-center mt-1">Появятся сообщения клиента и гипотезы</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-6">
      {timeline.map((item) => (
        item.type === 'message' ? (
          <MessageBubble key={`msg-${item.data.id}`} message={item.data} />
        ) : (
          <ProposalBubble key={`prop-${item.data.id}`} proposal={item.data} reviewActions={reviewActions} />
        )
      ))}
      {awaitingDraft && (
        <div className="flex items-center gap-2 rounded-2xl rounded-tr-md px-4 py-3 bg-accent-ai/10 border border-accent-ai/20 self-start">
          <Spinner size="sm" className="text-accent-ai" />
          <span className="text-sm text-text-secondary">AI готовит черновик...</span>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
